// Third-party
import { SortingRule } from 'react-table';

// Redux
import { AsyncResult, Action, ApiAction } from '../index';
import api from '../redux-api';

// Types
import { modelType } from '@/types/data';
import Portfolio from '@/types/portfolio';
import PortfolioHolding from '@/types/portfolio-holding';
import { CustomProxyMapping } from '@/types/app-config';
import { PortfolioFDC3 } from '@/types/portfolio-fdc3';
import { PortfoliosState } from './types/portfolios';

// Utils
import { LOCALE } from '../../utils/i18n';
import { PortfolioEditorContext, transformFDC3BasePortfolio, transformFDC3Positions } from '@/utils/portfolios';
import { initAvailableActionsToCopy } from '@/utils/availableActions';
import { createPortfolioClone } from '@/utils/portfolio/utils';
import { loadPortfolioForEdit, setPortfolioEditorContext } from './edit-portfolio';
import { ApiRequestConfig } from '@/utils/api';

// Initial state
const INITIAL_STATE: PortfoliosState = {
  models: [],
  myModels: [],
  enterpriseModels: [],
};

// Actions
const GET_MODELS_PORTFOLIOS: ApiAction = {
  type: 'connect/portfolios/models',
  endpoint: 'portfolios/models',
};

const GET_MY_MODELS_PORTFOLIOS: ApiAction = {
  type: 'connect/portfolios/my-models',
  endpoint: 'portfolios/models',
};

const GET_ENTERPRISE_MODELS_PORTFOLIOS: ApiAction = {
  type: 'connect/portfolios/enterprise-models',
  endpoint: 'portfolios/models',
};

export const GET_PORTFOLIO_BY_ID: ApiAction = {
  type: 'connect/portfolios/get-by-id',
  endpoint: 'portfolios/:id/',
};

const DELETE_PORTFOLIO: ApiAction = {
  type: 'connect/portfolios/delete',
  endpoint: 'portfolios/:id/',
};

export const DELETE_PORTFOLIOS: ApiAction = {
  type: 'connect/portfolios/delete-many',
  endpoint: '/portfolios/models',
};

export const GET_INSTRUMENTS_BY_CODE: ApiAction = {
  endpoint: 'instruments/by-code',
  type: 'portfolios/instruments-by-code',
};

const ADD_NEWLY_CREATED_MODEL_ID: Action = {
  type: 'connect/portfolios/add-newly-created-model-id',
};

const CLEAR_NEWLY_CREATED_MODEL_IDS: Action = {
  type: 'connect/portfolios/clear-newly-created-model-ids',
};

// Reducer
export default function reducer(state: PortfoliosState = INITIAL_STATE, action: Action): PortfoliosState {
  switch (action.type) {
    case `${GET_MODELS_PORTFOLIOS.type}_SUCCESS`: {
      const { portfolios, next } = action.data.data;
      return {
        ...state,
        models: action.params.next ? state.models.concat(portfolios) : portfolios,
        nextModelsToken: next,
      };
    }
    case `${GET_MY_MODELS_PORTFOLIOS.type}_SUCCESS`: {
      const { portfolios, next } = action.data.data;
      return {
        ...state,
        myModels: action.params.next ? state.myModels.concat(portfolios) : portfolios,
        nextMyModelsToken: next,
      };
    }
    case `${GET_ENTERPRISE_MODELS_PORTFOLIOS.type}_SUCCESS`: {
      const { portfolios, next } = action.data.data;
      return {
        ...state,
        enterpriseModels: action.params.next ? state.enterpriseModels.concat(portfolios) : portfolios,
        nextEnterpriseModelsToken: next,
      };
    }
    case `${DELETE_PORTFOLIOS.type}_SUCCESS`: {
      const ids = action.data.portfolioIds;
      return {
        ...state,
        models: state.models.filter(p => !ids.includes(p._id)),
        myModels: state.myModels.filter(p => !ids.includes(p._id)),
        enterpriseModels: state.enterpriseModels.filter(p => !ids.includes(p._id)),
      };
    }
    case ADD_NEWLY_CREATED_MODEL_ID.type: {
      const existingIds = state.newlyCreatedModelIds || [];
      if (!action.portfolioId || existingIds.includes(action.portfolioId)) {
        return state;
      }
      return {
        ...state,
        newlyCreatedModelIds: [...existingIds, action.portfolioId],
      };
    }
    case CLEAR_NEWLY_CREATED_MODEL_IDS.type: {
      return {
        ...state,
        newlyCreatedModelIds: undefined,
      };
    }
    default:
      return state;
  }
}

// Action Creators
export const deletePortfolios = (portfolios: Portfolio[]): AsyncResult<{ portfolioIds: string[] }> => {
  const params = {
    portfolioIds: portfolios.map(portfolio => portfolio._id).join(';'),
  };

  return () => api.delete(DELETE_PORTFOLIOS, params);
};

export const deletePortfolio =
  (portfolio: Portfolio): AsyncResult<void> =>
  () =>
    api.delete(DELETE_PORTFOLIO, { id: portfolio._id });

export const getCapIntelPortfolioById = (
  id: string,
  portfolioType?: string,
  contact?: string,
  enterprise?: any,
  errorWhiteList?: string[],
  options?: ApiRequestConfig,
): Promise<Portfolio> => {
  const params: { id: string; portfolioType?: string; contactId?: string; enterpriseId?: string } = {
    id,
    portfolioType,
  };
  if (contact) {
    params.contactId = contact;
  }
  if (enterprise) {
    params.enterpriseId = enterprise._id || enterprise;
  }
  return api.get(GET_PORTFOLIO_BY_ID, params, options, errorWhiteList).then((result: { portfolio: Portfolio }) => result.portfolio);
};

export const getPostMessagePortfolioById = (source: Portfolio): Promise<Portfolio> => Promise.resolve(source);

export const getPortfolioById =
  (
    id: string,
    portfolioType?: string,
    contact?: string,
    enterprise?: any,
    errorWhiteList?: string[],
    source?: Portfolio,
    options?: ApiRequestConfig,
  ): AsyncResult<Portfolio> =>
  () => {
    const isNewlyCreatedPortfolio = id.startsWith('new-') && source != null;
    if (isNewlyCreatedPortfolio) {
      return Promise.resolve(source);
    }
    return source?.isFromPostMessage
      ? getPostMessagePortfolioById(source)
      : getCapIntelPortfolioById(id, portfolioType, contact, enterprise, errorWhiteList, options);
  };

export const getModelsPortfolios =
  (type?: modelType, query?: string, sorting?: SortingRule[], nextToken?: string): AsyncResult<Portfolio[]> =>
  (dispatch, getState) => {
    const state = getState();
    const {
      auth: { user },
    } = state;
    let {
      intl: { locale },
    } = state;

    if (!user) {
      return Promise.reject();
    }

    // TODO - Support en-US filters [CON-8332]
    if (locale === LOCALE.EN_US) {
      locale = LOCALE.EN_CA;
    }

    const params: { query?: string; next?: string; sort?: string[]; modelType?: modelType; locale: string } = { locale };

    if (query) {
      params.query = query;
    }

    if (nextToken) {
      params.next = nextToken;
    }

    if (sorting) {
      params.sort = sorting.map((sort: SortingRule) => `${sort.id} ${sort.desc ? 'desc' : 'asc'}`);
    }

    let action = GET_MODELS_PORTFOLIOS;

    if (type) {
      params.modelType = type;

      switch (type) {
        case modelType.User:
          action = GET_MY_MODELS_PORTFOLIOS;
          break;
        case modelType.Enterprise:
          action = GET_ENTERPRISE_MODELS_PORTFOLIOS;
          break;
        default:
          action = GET_MODELS_PORTFOLIOS;
          break;
      }
    }

    return api.get(action, params).then((res: { data: { portfolios: Portfolio[] } }) => res.data.portfolios);
  };

export const transformFDC3Portfolio = async (
  fdc3Portfolio: PortfolioFDC3,
  mustIncludeHoldings: boolean = false,
  customProxyMapping?: CustomProxyMapping[],
): Promise<Portfolio> => {
  const portfolio: Portfolio = transformFDC3BasePortfolio(fdc3Portfolio);
  if (mustIncludeHoldings && fdc3Portfolio.positions && fdc3Portfolio.marketValue) {
    const instrumentsByCodePayload = transformFDC3Positions(fdc3Portfolio.positions, fdc3Portfolio.marketValue, customProxyMapping);
    const holdings: PortfolioHolding[] = (await api.post(GET_INSTRUMENTS_BY_CODE, null, instrumentsByCodePayload)).data?.instruments || [];
    portfolio.holdings = holdings;
  }
  portfolio.availableActions = initAvailableActionsToCopy();
  return portfolio;
};

export const clonePortfolio =
  (portfolio: Portfolio): AsyncResult<void> =>
  async (dispatch, getState) => {
    const portfolioClone = createPortfolioClone(portfolio);

    // When copying an enterprise model, we want to save it in My Models and not as an enterprise model.
    if (portfolioClone.enterprise) {
      portfolioClone.mine = true;
      delete portfolioClone.enterprise;
      delete portfolioClone.enterpriseGroups;
    }

    dispatch(setPortfolioEditorContext(PortfolioEditorContext.PORTFOLIO));
    dispatch(loadPortfolioForEdit(portfolioClone, undefined, false, true, false));
  };

export const addNewlyCreatedModelId =
  (portfolioId: string): Result<void> =>
  dispatch => {
    dispatch({ type: ADD_NEWLY_CREATED_MODEL_ID.type, portfolioId });
  };

export const clearNewlyCreatedModelIds = (): Result<void> => dispatch => {
  dispatch({ type: CLEAR_NEWLY_CREATED_MODEL_IDS.type });
};
